import { Check, Globe } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import type { SupportedLang } from "@/i18n/config";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const LANGS: { code: SupportedLang; label: string; native: string }[] = [
  { code: "fr", label: "French", native: "Français" },
  { code: "ar", label: "Arabic", native: "العربية" },
  { code: "en", label: "English", native: "English" },
];

function FlagIcon({ code }: { code: SupportedLang }) {
  const common = "block h-[18px] w-6 shrink-0 overflow-hidden rounded-[3px] shadow-sm ring-1 ring-border";

  if (code === "fr") {
    return (
      <svg viewBox="0 0 3 2" className={common} aria-hidden="true">
        <rect width="1" height="2" fill="#0055A4" />
        <rect x="1" width="1" height="2" fill="#FFFFFF" />
        <rect x="2" width="1" height="2" fill="#EF4135" />
      </svg>
    );
  }

  if (code === "ar") {
    return (
      <svg viewBox="0 0 30 20" className={common} aria-hidden="true">
        <rect width="30" height="20" fill="#C1272D" />
        <path
          d="M15 5.25 16.15 8.8h3.73l-3.02 2.2 1.15 3.55L15 12.35l-3.02 2.2L13.13 11l-3.02-2.2h3.73L15 5.25Z"
          fill="none"
          stroke="#006233"
          strokeWidth="1.35"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 60 36" className={common} aria-hidden="true">
      <g>
        <rect width="60" height="36" fill="#012169" />
        <path d="M0 0 60 36M60 0 0 36" stroke="#FFFFFF" strokeWidth="8" />
        <path d="M0 0 60 36M60 0 0 36" stroke="#C8102E" strokeWidth="4" />
        <path d="M30 0v36M0 18h60" stroke="#FFFFFF" strokeWidth="13" />
        <path d="M30 0v36M0 18h60" stroke="#C8102E" strokeWidth="7" />
      </g>
    </svg>
  );
}

export function LanguageSwitcher({ variant = "default" }: { variant?: "default" | "ghost" }) {
  const { lang, setLang } = useLang();
  const current = LANGS.find((l) => l.code === lang) ?? LANGS[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Language"
        className={cn(
          "inline-flex cursor-pointer items-center gap-2 rounded-full outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1",
          variant === "ghost"
            ? "px-2 py-1 hover:bg-accent"
            : "border border-border bg-background px-2.5 py-1.5 hover:bg-accent",
        )}
      >
        <FlagIcon code={current.code} />
        <Globe className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="min-w-[13rem] rounded-xl border-border/60 bg-background p-1.5 shadow-xl shadow-violet-500/10"
      >
        {LANGS.map((language) => {
          const isActive = language.code === lang;
          return (
            <DropdownMenuItem
              key={language.code}
              onSelect={() => setLang(language.code)}
              aria-current={isActive ? "true" : undefined}
              className={cn(
                "gap-3 rounded-lg px-3 py-2.5 text-sm font-medium outline-none transition-colors focus:bg-primary/10 focus:text-foreground",
                isActive && "bg-primary/10 text-primary",
              )}
            >
              <span className="flex shrink-0 items-center" aria-hidden="true">
                <FlagIcon code={language.code} />
              </span>
              <span
                dir={language.code === "ar" ? "rtl" : "ltr"}
                className="flex-1 text-left"
                lang={language.code}
              >
                {language.native}
              </span>
              <Check
                className={cn("h-4 w-4 shrink-0 text-primary", isActive ? "opacity-100" : "opacity-0")}
                aria-hidden="true"
              />
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
